class VariantSelector {
  constructor(container) {
    this.container = container;
    this.productCards = Array.from(
      container.querySelectorAll(".variant-product-card")
    );

    this.selectedVariant = null;

    this.callbacks = {
      onVariantSelect: [],
    };

    this.init();
  }

  init() {
    this.productCards.forEach((card, index) => {
      card.addEventListener("click", () => {
        this.selectVariant(index);
      });
    });

    /*
     * Find current variant from URL first.
     */
    const urlVariantId = new URLSearchParams(
      window.location.search
    ).get("variant");

    const section =
      this.container.closest(".shop-product-section") ||
      this.container.closest('[id^="shopify-section-"]') ||
      document;

    const productFormElement =
      section.querySelector("product-form");

    const form =
      productFormElement?.querySelector("form") ||
      section.querySelector('form[action*="/cart/add"]');

    const formVariantInput =
      form?.querySelector('[name="id"]');

    const formVariantId =
      formVariantInput?.value || null;

    const initialVariantId =
      urlVariantId || formVariantId;

    let initialIndex =
      this.productCards.findIndex((card) => {
        return (
          String(card.dataset.variantId) ===
          String(initialVariantId)
        );
      });

    if (initialIndex < 0) {
      initialIndex = 0;
    }

    this.setSelectedCard(initialIndex);
  }

  setSelectedCard(index) {
    this.productCards.forEach((card) => {
      card.classList.remove("selected");
    });

    const card =
      this.productCards[index];

    if (!card) return;

    card.classList.add("selected");

    this.selectedVariant = index;
  }

  selectVariant(index) {
    const card =
      this.productCards[index];

    if (!card) return;

    this.setSelectedCard(index);

    const variantData =
      this.getVariantData(index);

    if (!variantData) return;

    /*
     * Update Shopify form directly.
     */
    this.updateProductForm(
      variantData
    );

    /*
     * Update URL.
     */
    this.updateUrl(
      variantData.variantId
    );

    /*
     * Run optional callbacks.
     */
    this.callbacks.onVariantSelect.forEach(
      (callback) => {
        callback(
          variantData,
          index
        );
      }
    );

    /*
     * Custom Variant Bundle event.
     *
     * IMPORTANT:
     * We deliberately do NOT dispatch
     * "change" on Shopify's hidden ID input.
     */
    this.container.dispatchEvent(
      new CustomEvent(
        "variantSelected",
        {
          bubbles: true,

          detail: {
            variant: variantData,
            index: index,
            element: card,
          },
        }
      )
    );

    console.log(
      "Variant Bundle selected:",
      variantData.variantId,
      variantData.title,
      variantData.currentPrice
    );
  }

  updateProductForm(variantData) {
    if (!variantData?.variantId) {
      return;
    }

    const section =
      this.container.closest(
        ".shop-product-section"
      ) ||
      this.container.closest(
        '[id^="shopify-section-"]'
      ) ||
      document;

    /*
     * Get the exact product-form component
     * belonging to this section.
     */
    let productFormElement =
      section.querySelector(
        "product-form"
      );

    /*
     * Fallback in case Elixir structure
     * is slightly different.
     */
    if (!productFormElement) {
      productFormElement =
        document.querySelector(
          "product-form"
        );
    }

    let form =
      productFormElement?.querySelector(
        "form"
      );

    if (!form) {
      form =
        section.querySelector(
          'form[action*="/cart/add"]'
        );
    }

    if (!form) {
      console.error(
        "Variant Bundle: product form not found"
      );

      return;
    }

    const variantId =
      String(
        variantData.variantId
      );

    /*
     * Set ALL ID inputs inside this
     * exact product form.
     *
     * This protects against themes which
     * contain more than one id field.
     */
    const variantInputs =
      form.querySelectorAll(
        '[name="id"]'
      );

    if (!variantInputs.length) {
      console.error(
        "Variant Bundle: no variant ID input found",
        form
      );

      return;
    }

    variantInputs.forEach(
      (input) => {
        input.value =
          variantId;

        input.setAttribute(
          "value",
          variantId
        );
      }
    );

    /*
     * Do NOT dispatch:
     *
     * input.dispatchEvent(...)
     * change.dispatchEvent(...)
     *
     * Elixir's other variant scripts can
     * intercept those events and rerender
     * or reset this form.
     */

    /*
     * Make sure available variants
     * leave the ATC button enabled.
     */
    if (
      variantData.variantAvailable
    ) {
      const submitButton =
        productFormElement?.querySelector(
          '[type="submit"]'
        ) ||
        form.querySelector(
          '[type="submit"]'
        );

      if (submitButton) {
        submitButton.removeAttribute(
          "disabled"
        );

        submitButton.removeAttribute(
          "aria-disabled"
        );
      }
    }

    /*
     * Debug:
     * verify what Shopify will receive.
     */
    const finalId =
      form.querySelector(
        '[name="id"]'
      )?.value;

    console.log(
      "Shopify form variant ID:",
      finalId
    );
  }

  updateUrl(variantId) {
    if (
      !variantId ||
      !window.history?.replaceState
    ) {
      return;
    }

    const url =
      new URL(
        window.location.href
      );

    url.searchParams.set(
      "variant",
      variantId
    );

    window.history.replaceState(
      window.history.state || {},
      "",
      url.toString()
    );
  }

  getVariantData(index) {
    const card =
      this.productCards[index];

    if (!card) return null;

    return {
      title:
        card
          .querySelector(
            ".variant-product-title"
          )
          ?.textContent
          ?.trim() || "",

      description:
        card
          .querySelector(
            ".variant-product-description"
          )
          ?.textContent
          ?.trim() || "",

      originalPrice:
        card
          .querySelector(
            ".variant-original-price"
          )
          ?.textContent
          ?.trim() || "",

      currentPrice:
        card
          .querySelector(
            ".variant-current-price"
          )
          ?.textContent
          ?.trim() || "",

      hasBadge:
        !!card.querySelector(
          ".variant-best-value-badge"
        ),

      badgeText:
        card
          .querySelector(
            ".variant-best-value-badge"
          )
          ?.textContent
          ?.trim() || "",

      variantId:
        card.dataset.variantId ||
        null,

      productId:
        card.dataset.productId ||
        null,

      variantPrice:
        card.dataset.variantPrice ||
        null,

      variantComparePrice:
        card.dataset
          .variantComparePrice ||
        null,

      variantAvailable:
        card.dataset
          .variantAvailable ===
        "true",
    };
  }

  getSelectedVariant() {
    if (
      this.selectedVariant === null
    ) {
      return null;
    }

    return this.getVariantData(
      this.selectedVariant
    );
  }

  onVariantSelect(callback) {
    this.callbacks.onVariantSelect.push(
      callback
    );
  }

  updateVariantColors(colors) {
    if (!colors) return;

    Object.entries(
      colors
    ).forEach(
      ([property, value]) => {
        if (!value) return;

        this.container.style.setProperty(
          `--variant-${property.replace(
            /_/g,
            "-"
          )}`,
          value
        );
      }
    );
  }
}


/*
 * ==========================================
 * INITIALIZATION
 * ==========================================
 */

function initVariantSelectors(
  root = document
) {
  const containers =
    root.querySelectorAll
      ? root.querySelectorAll(
          ".variant-selector-container"
        )
      : [];

  containers.forEach(
    (container) => {
      if (
        container.variantSelector
      ) {
        return;
      }

      const selector =
        new VariantSelector(
          container
        );

      container.variantSelector =
        selector;
    }
  );
}


/*
 * Normal storefront load
 */
document.addEventListener(
  "DOMContentLoaded",
  () => {
    initVariantSelectors(
      document
    );
  }
);


/*
 * Shopify Theme Editor reload
 */
document.addEventListener(
  "shopify:section:load",
  (event) => {
    initVariantSelectors(
      event.target
    );
  }
);


/*
 * Public helper
 */
window.createVariantSelector =
  function (
    container,
    options = {}
  ) {
    const selector =
      new VariantSelector(
        container
      );

    if (options.colors) {
      selector.updateVariantColors(
        options.colors
      );
    }

    if (options.onSelect) {
      selector.onVariantSelect(
        options.onSelect
      );
    }

    return selector;
  };